// Values of these attributes carry the structure that locates a mismatch (class names, React ids,
// ARIA state) and never personal data. Every other attribute value is masked.
const KEPT_ATTRIBUTES = new Set([
	"class",
	"id",
	"role",
	"type",
	"name",
	"for",
	"rel",
	"target",
	"tabindex",
	"dir",
	"lang",
	"hidden",
	"disabled",
	"checked",
	"selected",
	"aria-hidden",
	"aria-expanded",
	"aria-selected",
	"aria-checked",
	"aria-disabled",
	"aria-current",
	"aria-haspopup",
	"aria-controls",
	"aria-labelledby",
	"aria-describedby",
]);

// Comments, and tags whose quoted attribute values are consumed whole so a `>` inside one cannot end the tag.
const MARKUP = /<!--[\s\S]*?(?:-->|$)|<\/?[A-Za-z][^\s/>]*(?:"[^"]*"|[^">])*>?/g;
const ATTRIBUTE = /([^\s"=/<>]+)="([^"]*)"/g;

const maskText = (text: string) =>
	text.replace(/\p{N}/gu, (digit) => "0".repeat(digit.length)).replace(/[\p{L}\p{M}]/gu, (letter) => "x".repeat(letter.length));

const maskAll = (value: string) => "x".repeat(value.length);

function maskAttributeValue(name: string, value: string): string {
	if (value.includes("@")) {
		return maskAll(value);
	}
	if (KEPT_ATTRIBUTES.has(name)) {
		return value;
	}
	// A page path is already on every APM event (the page URL); a tel:/mailto: link is not a path.
	if (name === "href" && /^(\/|https?:)/.test(value)) {
		const queryStart = value.search(/[?#]/);
		return queryStart < 0 ? value : value.slice(0, queryStart) + maskAll(value.slice(queryStart));
	}
	return maskAll(value);
}

// Keeps tag names, attribute names and structure; replaces text with `x`/`0` and masks attribute
// values. Length-preserving, so an index into the original HTML points at the same place in the result.
export function maskHtml(html: string): string {
	let masked = "";
	let textStart = 0;
	for (const match of html.matchAll(MARKUP)) {
		const markup = match[0];
		masked += maskText(html.slice(textStart, match.index));
		masked += markup.startsWith("<!--")
			? maskText(markup)
			: markup.replace(ATTRIBUTE, (_, name: string, value: string) => `${name}="${maskAttributeValue(name, value)}"`);
		textStart = match.index + markup.length;
	}
	return masked + maskText(html.slice(textStart));
}

// `useId` output: `_R_…_` from the server and `_r_…_` from a client render in the React Next.js bundles,
// `«R…»` / `«r…»` in React 19.1.
const REACT_ID = /«[Rr][^»]*»|_[Rr]_[0-9A-Za-z]*_/g;

// Taken on every page load, so it does no more than it must: scripts go because the RSC payload makes
// them huge. Everything that makes the two sides comparable waits in normalizeHtml for an actual error.
export function serializeBody(): string {
	const clone = document.body.cloneNode(true);
	if (!(clone instanceof HTMLElement)) {
		throw new Error("Cloning the body did not produce an element");
	}
	for (const node of clone.querySelectorAll("script, next-route-announcer")) {
		node.remove();
	}
	return clone.innerHTML;
}

// After a hydration error React discards the server DOM and client-renders it, and a client render of
// unchanged markup still serializes differently: it has no comment markers (Suspense boundaries, text
// separators), no <noscript> content and no streaming <template> placeholders, it sets some attributes
// in a different order (an <img>'s src last), its `useId` values have another format, and the browser
// writes a style set through the CSSOM in its own canonical form. Normalizing both sides to one shape
// leaves the real mismatch as the first difference. The HTML is re-parsed into a <template>, whose
// content is inert: nothing in it loads or runs.
export function normalizeHtml(html: string): string {
	const template = document.createElement("template");
	template.innerHTML = html;
	const content = template.content;
	for (const node of content.querySelectorAll("template")) {
		node.remove();
	}
	for (const noscript of content.querySelectorAll("noscript")) {
		noscript.replaceChildren();
	}
	for (const element of content.querySelectorAll<HTMLElement>("[style]")) {
		element.setAttribute("style", element.style.cssText);
	}
	// Headless UI's Dialog and Popover render this empty hidden <span> to find their main tree and remove it from
	// a ref callback once mounted, so the server HTML has one per closed dialog and the DOM an error is captured
	// from has none. The selector matches the canonical style the loop above writes, so it has to run after it.
	for (const sentinel of content.querySelectorAll('span[hidden]:empty[style^="position: fixed; top: 1px; left: 1px; width: 1px; height: 0px;"]')) {
		sentinel.remove();
	}
	for (const element of content.querySelectorAll("*")) {
		const attributes = [...element.attributes];
		for (const attribute of attributes) {
			element.removeAttributeNode(attribute);
		}
		for (const attribute of attributes.sort((a, b) => (a.name < b.name ? -1 : 1))) {
			// A client render sets these as properties (and focuses instead of setting autofocus), never as attributes.
			if (attribute.name === "muted" || attribute.name === "autofocus") {
				continue;
			}
			attribute.value = attribute.value.replace(REACT_ID, "«id»");
			element.setAttributeNodeNS(attribute);
		}
	}
	const comments = document.createTreeWalker(content, NodeFilter.SHOW_COMMENT);
	const commentNodes: Node[] = [];
	while (comments.nextNode()) {
		commentNodes.push(comments.currentNode);
	}
	for (const comment of commentNodes) {
		comment.parentNode?.removeChild(comment);
	}
	return template.innerHTML;
}

// Positions of the server-side elements left over once each client element is paired with the next
// server element carrying the same signature. A client element with no such partner is skipped rather
// than allowed to consume the rest of the server side.
export function unpairedServerIndexes(serverSignatures: string[], clientSignatures: string[]): number[] {
	const unpaired: number[] = [];
	let next = 0;
	for (const signature of clientSignatures) {
		const partner = serverSignatures.indexOf(signature, next);
		if (partner < 0) {
			continue;
		}
		for (let index = next; index < partner; index++) {
			unpaired.push(index);
		}
		next = partner + 1;
	}
	return unpaired;
}

const signature = (element: Element) => `${element.tagName}#${element.id}.${element.getAttribute("class") ?? ""}`;

// React skips elements it did not render among <body>'s children instead of failing on them, so one a
// crawler, an extension or a third-party script inserted there before hydration is never the mismatch.
// It still vanishes when React client-renders the body after the error, and would otherwise be
// reported as the first difference. Takes and returns normalized HTML.
export function withoutForeignBodyChildren(serverHtml: string, clientHtml: string): string {
	const parse = (html: string) => {
		const template = document.createElement("template");
		template.innerHTML = html;
		return template;
	};
	const server = parse(serverHtml);
	const serverChildren = [...server.content.children];
	const clientChildren = [...parse(clientHtml).content.children];
	for (const index of unpairedServerIndexes(serverChildren.map(signature), clientChildren.map(signature))) {
		serverChildren[index].remove();
	}
	return server.innerHTML;
}

const VOID_ELEMENTS = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "source", "track", "wbr"]);
const TAG = /<(\/?)([A-Za-z][^\s/>]*)((?:"[^"]*"|[^">])*)>/g;

// The elements still open at `index`, e.g. `main#main-content > section.grid.gap-4 > p.price`, so the
// divergence can be placed in the tree however little of it the context snippets show.
export function elementPath(html: string, index: number): string {
	const open: string[] = [];
	for (const [, closing, tag, attributes] of html.slice(0, index).matchAll(TAG)) {
		if (closing) {
			open.pop();
			continue;
		}
		if (VOID_ELEMENTS.has(tag.toLowerCase())) {
			continue;
		}
		const id = /\sid="([^"]*)"/.exec(attributes)?.[1];
		const classes = (/\sclass="([^"]*)"/.exec(attributes)?.[1] ?? "").split(/\s+/).filter(Boolean).slice(0, 3);
		open.push(`${tag}${id ? `#${id}` : ""}${classes.map((className) => `.${className}`).join("")}`);
	}
	return open.join(" > ");
}

// The divergence is located on the raw HTML (masking would hide a text-only difference such as a
// timestamp); the path and the context around it come from the masked HTML.
export function firstDiff(serverHtml: string, clientHtml: string): { index: number; path: string; server: string; client: string } | null {
	const context = 500;

	let index = 0;
	while (index < serverHtml.length && index < clientHtml.length && serverHtml[index] === clientHtml[index]) {
		index++;
	}
	if (index === serverHtml.length && index === clientHtml.length) {
		return null;
	}

	const maskedServerHtml = maskHtml(serverHtml);
	const around = (maskedHtml: string) => maskedHtml.slice(Math.max(0, index - context), index + context);
	return { index, path: elementPath(maskedServerHtml, index), server: around(maskedServerHtml), client: around(maskHtml(clientHtml)) };
}
